import { useState, useRef, useEffect } from "react";
import { connect } from "react-redux";
import { useMountEffect } from "primereact/hooks";
import { Button } from "primereact/button";
import { useIntl } from "react-intl";
import { sectionListRequest } from "../../../../store/slices/customFieldSlice";
import {
  departmentRequest,
  designationRequest,
} from "../../../../store/slices/metaSlice";
import { ListBox } from "primereact/listbox";
import { GlobalConstants } from "../../../../constants";
import { MultiSelect } from "primereact/multiselect";

import "./user-filter.scss";

import FiltersSheet from "./FiltersSheet";
import useMobileView from "../../../common/hooks/useMobileView";

const UserFilters = ({
  departmentRequest,
  departments = [],
  designationRequest,
  designations = [],
  sectionListRequest,
  sections,
  onFilterChange = () => {},
  filters = [],
}) => {
  const intl = useIntl();
  const { isMobile } = useMobileView();

  const [isOpen, setIsOpen] = useState(false);
  const [isFilterSheetVisible, setIsFilterSheetVisible] = useState(false);
  const ref = useRef(null);

  const handleOutsideClick = (event) => {
    if (ref?.current && !ref?.current?.contains(event.target)) {
      setIsOpen(false); // Close the dropdown
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.addEventListener("mousedown", handleOutsideClick);
    } else {
      document.removeEventListener("mousedown", handleOutsideClick);
    }

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [isOpen]);

  useEffect(() => {
    if (isFilterSheetVisible && !isMobile) {
      setIsFilterSheetVisible(false);
    }
  }, [isMobile]);

  useMountEffect(() => {
    departmentRequest();
    designationRequest();
    sectionListRequest();
  });

  const customFields = sections.reduce((acc, section) => {
    acc = [
      ...acc,
      ...(section?.fields?.map((item) => ({
        id: item.id,
        name: item?.name,
        value: item.id,
        type: item.type,
        options: item?.options?.map((option) => ({
          id: option,
          name: option,
          value: option,
        })),
      })) || []),
    ];
    return acc;
  }, []);

  const allFields = [
    {
      id: "Designation",
      name: intl.formatMessage({ id: "designation" }),
      value: "Designation",
      type: "Dropdown",
      i18n: true,
      options: designations?.map((item) => ({
        name: item?.name || item.value,
        id: item.id,
        i18n: false,
        value: item.id,
      })),
    },
    {
      id: "Department",
      name: intl.formatMessage({ id: "department" }),
      value: "Department",
      type: "Dropdown",
      i18n: true,
      options: departments?.map((item) => ({
        name: item.name || item.value,
        id: item.id,
        i18n: false,
        value: item.id,
      })),
    },
    ...customFields
      .filter((item) => item.type !== 5)
      .map((item) => ({
        ...item,
        type: GlobalConstants.FIELD_TYPES.find((type) => type.id === item.type)
          ?.name,
      })),
  ];

  const availableFields = allFields.filter(
    (item) => !filters.find((field) => field.id === item.id)
  );

  return (
    <>
      <div ref={ref} className="edexlms-filters users">
        <div className="hidden md:block">
          {(availableFields.length > 0 && (
            <>
              <Button
                outlined
                onClick={() => setIsOpen(!isOpen)}
                label={intl.formatMessage({ id: "add_filter" })}
                icon="pi pi-plus"
              />
              {(availableFields.length > 0 && isOpen && (
                <div className="edexlms-listbox-container">
                  <ListBox
                    className="edexlms-listbox"
                    options={availableFields}
                    optionLabel="name"
                    optionValue="id"
                    listStyle={{ maxHeight: "250px" }}
                    onChange={(e) => {
                      const field = allFields.find(
                        (item) => item.id === e.value
                      );
                      if (filters.find((item) => item.id === field.id)) return;
                      setIsOpen(false);
                      onFilterChange([
                        ...filters,
                        {
                          id: field?.id,
                          options: field?.options,
                          type: field?.type,
                          name: field?.name,
                          values: [],
                        },
                      ]);
                    }}
                  />
                </div>
              )) ||
                null}
            </>
          )) ||
            null}
        </div>
        <div className="md:hidden">
          <Button
            onClick={() => setIsFilterSheetVisible(true)}
            className="flex align-items-center gap-1 relative overflow-visible"
          >
            {filters.filter((item) => item.values.length > 0).length > 0 && (
              <div
                style={{
                  position: "absolute",
                  top: -6,
                  right: -6,
                  backgroundColor: "red",
                  width: 20,
                  height: 20,
                  borderRadius: "100%",
                }}
              >
                <span>
                  {filters.filter((item) => item.values.length > 0).length}
                </span>
              </div>
            )}
            Filters
          </Button>
        </div>
      </div>
      <div className="hidden md:flex flex-wrap gap-2">
        {filters.map((field, index) => {
          return (
            <div className="edexlms-filter-item" key={index}>
              <MultiSelect
                value={field.values}
                filter={true}
                options={field.options.filter((option) => option.id)}
                optionLabel="name"
                optionValue="id"
                fixedPlaceholder={true}
                placeholder={field?.name}
                label={field?.name}
                onChange={(e) => {
                  const values = e.value;
                  onFilterChange(
                    filters.map((item) =>
                      item.id === field.id ? { ...item, values: values } : item
                    )
                  );
                }}
              />
              <Button
                outlined
                icon="pi pi-times"
                onClick={() =>
                  onFilterChange(filters.filter((item) => item.id !== field.id))
                }
              />
            </div>
          );
        })}
      </div>
      {isFilterSheetVisible && (
        <FiltersSheet
          allFields={allFields}
          filters={filters}
          handleClose={() => setIsFilterSheetVisible(false)}
          isFilterSheetVisible={isFilterSheetVisible}
          onFilterChange={onFilterChange}
        />
      )}
    </>
  );
};

const mapStateToProps = (state) => ({
  departments: state.meta.departments,
  designations: state.meta.designations,
  sections: state.customFields.data,
});

const mapDispatchToProps = (dispatch) => ({
  sectionListRequest: () => dispatch(sectionListRequest()),
  departmentRequest: () => dispatch(departmentRequest()),
  designationRequest: () => dispatch(designationRequest()),
});

export default connect(mapStateToProps, mapDispatchToProps)(UserFilters);
